import { InvoiceModel } from '../../Models/InvoiceModel';
import { InvoiceRemarks } from '../../Models/InvoiceRemarks';
import NomenclatureExclusion from '../../Models/NomenclatureExclusion';
import NonAllowedOfficeHourSupplementRemark from '../../Models/NonAllowedOfficeHourSupplementRemark';
import OnceADayGroupRemark from '../../Models/OnceADayGroupRemark';
import OncePerIterationRemark from '../../Models/OncePerIterationRemark';
import OncePerNrCalendarYearsGroupRemark from '../../Models/OncePerNrCalendarYearsGroupRemark';
import RecentOrder from '../../Models/RecentOrder';
import TooMuchNonAmountSupplementRemark from '../../Models/TooMuchNonAmountSupplementRemark';

export class InvoiceModelMapper {
    map(model: InvoiceModel): InvoiceModel {
        return new InvoiceModel(
            model.AllOrderNomenclatures,
            model.CustomHonorarium,
            model.CustomSupplementHonorarium,
            model.CustomWIGW,
            model.CustomSupplementWIGW,
            model.CustomOther,
            model.CustomSupplementOther,
            model.CustomPractice,
            model.CustomSupplementPractice,
            model.CompanyPays,
            model.AmountPatient,
            model.TotalAmount,
            model.ThirdPayerPatientShare,
            model.ThirdPayerHealthInsuranceShare,
            new InvoiceRemarksMapper().map(model.Remarks),
            model.FreeOfCharge,
            model.ProcedureAndProductNomenclatures,
            model.TotalHonorarium,
            model.TotalWIGW,
            model.TotalOther,
            model.TotalPractice,
            model.PaymentMethod,
            model.PatientStatus,
            model.DateOfImplementationString,
        );
    }
}

class InvoiceRemarksMapper {
    map(remarks: InvoiceRemarks): InvoiceRemarks {
        const oncePerIterationRemarkMapper = new OncePerIterationRemarkMapper();

        return new InvoiceRemarks(
            new NomenclatureExclusionMapper().mapExclusions(remarks?.Exclusions),
            oncePerIterationRemarkMapper.mapIterationRemarks(remarks?.OnceADayRemarks, 'IsOnlyAllowedOnceADay'),
            new OnceADayGroupRemarkMapper().mapOnceADayGroupRemarks(remarks?.OnceADayGroupRemarks),
            oncePerIterationRemarkMapper.mapIterationRemarks(remarks?.OncePerNrCalendarYearsRemarks, 'IsOnlyAllowedOncePerNrYears'),
            new OncePerNrCalendarYearsGroupRemarkMapper().mapOncePerNrCalendarYearsGroupRemarks(remarks?.OncePerNrCalendarYearsGroupRemarks),
            oncePerIterationRemarkMapper.mapIterationRemarks(remarks?.OncePerNrOfMonthsRemarks, 'IsOnlyAllowedOncePerNrMonths'),
            remarks?.HadForfait,
            remarks?.HadConsultance,
            remarks?.HadSupplement,
            remarks?.HadNomenclatureWithAgeOutOfRange,
            remarks?.HasHigherAmountSupplement,
            new TooMuchNonAmountSupplementRemarkMapper().mapTooMuchNonAmountSupplementRemarks(remarks?.MultiplePercentageOrCalculated),
            remarks?.MultipleCustomAmountSupplements,
            new RecentOrderMapper().mapRecentOrders(remarks?.RecentOrders),
            new NonAllowedOfficeHourSupplementRemarksMapper().mapNonAllowedOfficeHourSupplementRemarks(remarks.NonAllowedOfficeHourSupplementRemarks),
            remarks?.HadReducedSupplement,
            remarks?.OrphanedNomenclatureNumbers ?? [],
        );
    }
}

class NomenclatureExclusionMapper {
    mapExclusions(exclusions: NomenclatureExclusion[]): NomenclatureExclusion[] {
        const mappedExclusions: NomenclatureExclusion[] = [];
        exclusions?.forEach((x) => {
            mappedExclusions.push(this.mapExclusion(x));
        });

        return mappedExclusions;
    }

    mapExclusion(exclusion: NomenclatureExclusion): NomenclatureExclusion {
        return new NomenclatureExclusion(
            exclusion.GroupExclusion,
            exclusion.Nomenclature1,
            exclusion.ProcedureCode1,
            exclusion.Nomenclature2,
            exclusion.ProcedureCode2,
            exclusion.Information,
        );
    }
}

class OncePerIterationRemarkMapper {
    mapIterationRemarks(remarks: OncePerIterationRemark[], restrictionKey: string): OncePerIterationRemark[] {
        const mappedRemarks: OncePerIterationRemark[] = [];
        remarks?.forEach((x) => {
            x.RestrictionKey = restrictionKey;
            mappedRemarks.push(this.mapIterationRemark(x));
        });

        return mappedRemarks;
    }

    mapIterationRemark(remark: OncePerIterationRemark): OncePerIterationRemark {
        return new OncePerIterationRemark(
            remark.NomenclatureNumber,
            remark.ProcedureCode,
            remark.Date,
            remark.PerIteration,
            remark.OrderID,
            remark.InvoiceID,
            remark.RestrictionKey,
            remark.InvoiceNumber,
            remark.OrderNumber,
        );
    }
}

class OnceADayGroupRemarkMapper {
    mapOnceADayGroupRemarks(remarks: OnceADayGroupRemark[]): OnceADayGroupRemark[] {
        const mappedRemarks: OnceADayGroupRemark[] = [];
        remarks?.forEach((x) => {
            mappedRemarks.push(this.mapOnceADayGroupRemark(x));
        });

        return mappedRemarks;
    }

    mapOnceADayGroupRemark(remark: OnceADayGroupRemark): OnceADayGroupRemark {
        return new OnceADayGroupRemark(
            remark.NomenclatureNumber,
            remark.ProcedureCode,
            remark.Date,
            remark.PerIteration,
            remark.OrderID,
            remark.InvoiceID,
            remark.OtherNomenclatureNumber,
            remark.OtherProcedureCode,
        );
    }
}

class OncePerNrCalendarYearsGroupRemarkMapper {
    mapOncePerNrCalendarYearsGroupRemarks(remarks: OncePerNrCalendarYearsGroupRemark[]): OncePerNrCalendarYearsGroupRemark[] {
        const mappedRemarks: OncePerNrCalendarYearsGroupRemark[] = [];
        remarks?.forEach((x) => {
            mappedRemarks.push(this.mapOncePerNrCalendarYearsGroupRemark(x));
        });

        return mappedRemarks;
    }

    mapOncePerNrCalendarYearsGroupRemark(remark: OncePerNrCalendarYearsGroupRemark): OncePerNrCalendarYearsGroupRemark {
        return new OncePerNrCalendarYearsGroupRemark(
            remark.NomenclatureNumber,
            remark.ProcedureCode,
            remark.Date,
            remark.PerIteration,
            remark.OrderID,
            remark.InvoiceID,
            remark.OtherNomenclatureNumber,
            remark.OtherProcedureCode,
        );
    }
}

class TooMuchNonAmountSupplementRemarkMapper {
    mapTooMuchNonAmountSupplementRemarks(remarks: TooMuchNonAmountSupplementRemark[]): TooMuchNonAmountSupplementRemark[] {
        const mappedRemarks: TooMuchNonAmountSupplementRemark[] = [];
        remarks?.forEach((x) => {
            mappedRemarks.push(this.mapTooMuchNonAmountSupplementRemark(x));
        });

        return mappedRemarks;
    }

    mapTooMuchNonAmountSupplementRemark(remark: TooMuchNonAmountSupplementRemark): TooMuchNonAmountSupplementRemark {
        return new TooMuchNonAmountSupplementRemark(
            remark.NomenclatureNumber,
            remark.SupplementType,
        );
    }
}

class RecentOrderMapper {
    mapRecentOrders(orders: RecentOrder[]): RecentOrder[] {
        const mappedOrders: RecentOrder[] = [];
        orders?.forEach((x) => {
            mappedOrders.push(this.mapRecentOrder(x));
        });

        return mappedOrders;
    }

    mapRecentOrder(order: RecentOrder): RecentOrder {
        return new RecentOrder(
            order.OrderID,
            order.ProcedureCode,
            order.OrderNumber,
            order.DateTimeImplementation,
        );
    }
}

class NonAllowedOfficeHourSupplementRemarksMapper {
    mapNonAllowedOfficeHourSupplementRemarks(remarks: NonAllowedOfficeHourSupplementRemark[]): NonAllowedOfficeHourSupplementRemark[] {
        const mappedRemarks: NonAllowedOfficeHourSupplementRemark[] = [];
        remarks?.forEach((x) => {
            mappedRemarks.push(this.mapNonAllowedOfficeHourSupplementRemark(x));
        });

        return mappedRemarks;
    }

    mapNonAllowedOfficeHourSupplementRemark(remark: NonAllowedOfficeHourSupplementRemark): NonAllowedOfficeHourSupplementRemark {
        return new NonAllowedOfficeHourSupplementRemark(
            remark.ProcedureCode,
            remark.NomenclatureNumber,
        );
    }
}
